import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  formatJakartaEndOfDay,
  formatJakartaStartOfDay,
} from "../../utils/date";
import {
  forceLogoutToLogin,
  isUnauthorizedApiResponse,
} from "../../utils/unauthorized";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "/api";

const parseEnvelope = async (response) => {
  const isJson = response.headers
    .get("content-type")
    ?.toLowerCase()
    .includes("application/json");
  const payload = isJson ? await response.json() : null;

  if (isUnauthorizedApiResponse(response, payload)) {
    forceLogoutToLogin();
  }

  const allowedCodes = new Set([200, 201, 204]);

  if (!payload || !allowedCodes.has(payload.resultCode)) {
    let message =
      payload?.resultDesc ||
      (response.ok ? "Unexpected response from server." : response.statusText);

    if (payload?.errors) {
      const errorMessages = Object.values(payload.errors)
        .flat()
        .filter(Boolean);
      if (errorMessages.length) {
        message = errorMessages.join(", ");
      }
    }

    const error = new Error(message);
    error.payload = payload;
    throw error;
  }

  if (payload.data !== undefined) {
    return payload.data;
  }

  return payload;
};

const getAuthHeaders = (state) => {
  const token = state.auth?.accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const buildQueryParams = (eventsState) => {
  const params = new URLSearchParams();
  params.append("page", eventsState.pagination.page + 1);
  params.append("pageSize", eventsState.pagination.pageSize);
  params.append("sortBy", eventsState.sort.field);
  params.append("sortDirection", eventsState.sort.direction);

  if (eventsState.filters.search.trim()) {
    params.append("search", eventsState.filters.search.trim());
  }
  if (eventsState.filters.status && eventsState.filters.status !== "ALL") {
    params.append("status", eventsState.filters.status);
  }
  if (eventsState.filters.startDate) {
    params.append(
      "startDate",
      formatJakartaStartOfDay(eventsState.filters.startDate)
    );
  }
  if (eventsState.filters.endDate) {
    params.append(
      "endDate",
      formatJakartaEndOfDay(eventsState.filters.endDate)
    );
  }

  return params.toString();
};

export const fetchEvents = createAsyncThunk(
  "events/fetchList",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const query = buildQueryParams(state.events);
      const response = await fetch(`${API_BASE_URL}/events?${query}`, {
        headers: {
          Accept: "application/json",
          ...getAuthHeaders(state),
        },
      });

      return await parseEnvelope(response);
    } catch (error) {
      return rejectWithValue(error.message || "Failed to load events.");
    }
  }
);

export const fetchEventDetail = createAsyncThunk(
  "events/fetchDetail",
  async (eventId, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const response = await fetch(`${API_BASE_URL}/events/${eventId}`, {
        headers: {
          Accept: "application/json",
          ...getAuthHeaders(state),
        },
      });

      return await parseEnvelope(response);
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch event details.");
    }
  }
);

export const createEvent = createAsyncThunk(
  "events/create",
  async (payload, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const response = await fetch(`${API_BASE_URL}/events`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(state),
        },
        body: JSON.stringify(payload),
      });

      return await parseEnvelope(response);
    } catch (error) {
      return rejectWithValue(error.message || "Failed to create event.");
    }
  }
);

export const updateEvent = createAsyncThunk(
  "events/update",
  async ({ id, payload }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const response = await fetch(`${API_BASE_URL}/events/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(state),
        },
        body: JSON.stringify(payload),
      });

      return await parseEnvelope(response);
    } catch (error) {
      return rejectWithValue(error.message || "Failed to update event.");
    }
  }
);

export const updateEventStatus = createAsyncThunk(
  "events/updateStatus",
  async ({ id, status }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const response = await fetch(`${API_BASE_URL}/events/${id}/status`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(state),
        },
        body: JSON.stringify({ status }),
      });

      return await parseEnvelope(response);
    } catch (error) {
      return rejectWithValue(error.message || "Failed to update status.");
    }
  }
);

const initialMeta = {
  page: 1,
  pageSize: 10,
  totalItems: 0,
  totalPages: 0,
  statusCounts: { ALL: 0, ACTIVE: 0, INACTIVE: 0 },
};

const initialState = {
  items: [],
  entities: {},
  filters: {
    search: "",
    status: "ALL",
    startDate: null,
    endDate: null,
  },
  sort: {
    field: "created_datetime",
    direction: "desc",
  },
  pagination: {
    page: 0,
    pageSize: 10,
  },
  meta: initialMeta,
  loading: false,
  error: null,
  detailDialogEventId: null,
  detail: {
    loading: false,
    error: null,
  },
  formStatus: "idle",
  formError: null,
  statusUpdates: {},
};

const eventsSlice = createSlice({
  name: "events",
  initialState,
  reducers: {
    setSearchFilter: (state, action) => {
      state.filters.search = action.payload;
      state.pagination.page = 0;
    },
    setStatusFilter: (state, action) => {
      state.filters.status = (action.payload || "ALL").toUpperCase();
      state.pagination.page = 0;
    },
    setDateRangeFilter: (state, action) => {
      state.filters.startDate = action.payload.startDate || null;
      state.filters.endDate = action.payload.endDate || null;
      state.pagination.page = 0;
    },
    resetFilters: (state) => {
      state.filters = { ...initialState.filters };
      state.pagination.page = 0;
    },
    setSort: (state, action) => {
      state.sort = action.payload;
    },
    setPage: (state, action) => {
      state.pagination.page = action.payload;
    },
    setPageSize: (state, action) => {
      state.pagination.pageSize = action.payload;
      state.pagination.page = 0;
    },
    openDetailDialog: (state, action) => {
      state.detailDialogEventId = action.payload;
      state.detail.error = null;
    },
    closeDetailDialog: (state) => {
      state.detailDialogEventId = null;
      state.detail.error = null;
      state.detail.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEvents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEvents.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload || {};
        const items = Array.isArray(payload.items) ? payload.items : [];
        const meta = payload.meta || initialMeta;

        state.items = items;
        state.meta = {
          ...initialMeta,
          ...meta,
          statusCounts: {
            ALL: meta.statusCounts?.ALL ?? items.length,
            ACTIVE: meta.statusCounts?.ACTIVE ?? 0,
            INACTIVE: meta.statusCounts?.INACTIVE ?? 0,
          },
        };

        state.entities = items.reduce((acc, event) => {
          if (event?.id != null) {
            acc[event.id] = event;
          }
          return acc;
        }, { ...state.entities });

        if (typeof state.meta.page === "number") {
          state.pagination.page = Math.max(state.meta.page - 1, 0);
        }
        if (typeof state.meta.pageSize === "number") {
          state.pagination.pageSize = state.meta.pageSize;
        }
      })
      .addCase(fetchEvents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load events.";
      })
      .addCase(fetchEventDetail.pending, (state) => {
        state.detail.loading = true;
        state.detail.error = null;
      })
      .addCase(fetchEventDetail.fulfilled, (state, action) => {
        state.detail.loading = false;
        state.detail.error = null;
        const event = action.payload;
        if (event) {
          state.entities[event.id] = event;
        }
      })
      .addCase(fetchEventDetail.rejected, (state, action) => {
        state.detail.loading = false;
        state.detail.error = action.payload || "Failed to load event detail.";
      })
      .addCase(createEvent.pending, (state) => {
        state.formStatus = "loading";
        state.formError = null;
      })
      .addCase(createEvent.fulfilled, (state) => {
        state.formStatus = "succeeded";
      })
      .addCase(createEvent.rejected, (state, action) => {
        state.formStatus = "failed";
        state.formError = action.payload || "Failed to create event.";
      })
      .addCase(updateEvent.pending, (state) => {
        state.formStatus = "loading";
        state.formError = null;
      })
      .addCase(updateEvent.fulfilled, (state, action) => {
        state.formStatus = "succeeded";
        const event = action.payload;
        if (event) {
          state.entities[event.id] = event;
          state.items = state.items.map((item) =>
            item.id === event.id ? event : item
          );
        }
      })
      .addCase(updateEvent.rejected, (state, action) => {
        state.formStatus = "failed";
        state.formError = action.payload || "Failed to update event.";
      })
      .addCase(updateEventStatus.pending, (state, action) => {
        const id = action.meta.arg.id;
        state.statusUpdates[id] = "loading";
      })
      .addCase(updateEventStatus.fulfilled, (state, action) => {
        const event = action.payload;
        if (event?.id) {
          state.entities[event.id] = {
            ...(state.entities[event.id] || {}),
            ...event,
          };
          state.items = state.items.map((item) =>
            item.id === event.id ? { ...item, ...event } : item
          );
          state.statusUpdates[event.id] = "idle";
        } else {
          state.statusUpdates[action.meta.arg.id] = "idle";
        }
      })
      .addCase(updateEventStatus.rejected, (state, action) => {
        const id = action.meta.arg.id;
        state.statusUpdates[id] = "failed";
        state.error = action.payload || "Failed to update status.";
      });
  },
});

export const {
  setSearchFilter,
  setStatusFilter,
  setDateRangeFilter,
  resetFilters,
  setSort,
  setPage,
  setPageSize,
  openDetailDialog,
  closeDetailDialog,
} = eventsSlice.actions;

export const selectEventsState = (state) => state.events;
export const selectEventItems = (state) => state.events.items;
export const selectEventsMeta = (state) => state.events.meta;
export const selectEventsFilters = (state) => state.events.filters;
export const selectEventsSort = (state) => state.events.sort;
export const selectEventsPagination = (state) => state.events.pagination;
export const selectEventsLoading = (state) => state.events.loading;
export const selectEventsError = (state) => state.events.error;
export const selectEventStatusCounts = (state) =>
  state.events.meta.statusCounts || initialMeta.statusCounts;
export const selectDetailDialogEventId = (state) =>
  state.events.detailDialogEventId;
export const selectEventById = (state, id) => state.events.entities[id];
export const selectDetailState = (state) => state.events.detail;
export const selectStatusUpdateMap = (state) => state.events.statusUpdates;

export default eventsSlice.reducer;
